// Constants
import { ElementIds } from "@ms/uno-constants/lib/local/UIConstants";
// Controls
import { Button, Link, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, mergeClasses, Tooltip } from "@fluentui/react-components";
import { Add, ChevronDown, Dismiss, PortfolioFolderBundled } from "@ms/planner-generic-controls/lib/local/icons";
import { Popover } from "@ms/planner-generic-controls/lib/local/popover/Popover";
// Styles
import { menuPositioning, useLeftNavNewPlanSectionStyles } from "./LeftNavNewPlanSection.styles";
// Utilities
import { LearnMoreLinks } from "@ms/uno-constants/lib/local/AppConstants";
import React from "react";
import { Strings } from "../../../utilities/CultureUtilities";
/**
 * Enumeration of the split menu options for LeftNav new plan split button
 */ export var LeftNavNewPlanSplitMenuOptions = /*#__PURE__*/ function(LeftNavNewPlanSplitMenuOptions) {
    /** New plan option */ LeftNavNewPlanSplitMenuOptions["Plan"] = "Plan";
    /** New portfolio option */ LeftNavNewPlanSplitMenuOptions["Portfolio"] = "Portfolio";
    return LeftNavNewPlanSplitMenuOptions;
}({});
/**
 * LeftNav new plan section
 * @param props - component props
 */ export function LeftNavNewPlanSection({ newPlanInfo, isLeftNavCollapsed, keyCodeMapper, calloutDetails }) {
    const styles = useLeftNavNewPlanSectionStyles();
    const portfolioMenuItemRef = React.useRef(null);
    const [isMenuOpen, setIsMenuOpen] = React.useState(false);
    const name = Strings.LeftNav_CreateAPlanButtonText;
    // Render new plan button
    const renderNewPlanButton = (isSplitButtonEnabled)=>/*#__PURE__*/ React.createElement(Tooltip, {
            content: name,
            relationship: "label"
        }, /*#__PURE__*/ React.createElement(Button, {
            appearance: "primary",
            size: "medium",
            className: mergeClasses(styles.newPlanButtonBase, isSplitButtonEnabled ? styles.newPlanButtonWithSplit : styles.newPlanButton),
            onClick: ()=>newPlanInfo.onSelect("Plan"),
            icon: /*#__PURE__*/ React.createElement(Add, {
                className: styles.buttonIcon
            }),
            "aria-label": name
        }, !isLeftNavCollapsed && /*#__PURE__*/ React.createElement("span", {
            className: styles.buttonText
        }, name)));
    // Render new plan button when split is disabled
    if (!newPlanInfo.isSplitButtonEnabled) {
        return /*#__PURE__*/ React.createElement("div", {
            className: styles.newPlanSection,
            id: ElementIds.LeftNavNewPlanButton
        }, renderNewPlanButton(false));
    }
    // Render new plan button with menu options
    return /*#__PURE__*/ React.createElement("div", {
        className: styles.newPlanSection,
        id: ElementIds.LeftNavNewPlanButton
    }, /*#__PURE__*/ React.createElement("div", {
        className: styles.splitButtonContainer
    }, renderNewPlanButton(true), /*#__PURE__*/ React.createElement(Menu, {
        positioning: menuPositioning,
        open: isMenuOpen || newPlanInfo.isNewPortfolioCalloutVisible,
        onOpenChange: (event, data)=>{
            // Prevent menu from closing when callout is visible
            if (!data.open && newPlanInfo.isNewPortfolioCalloutVisible) {
                return;
            }
            setIsMenuOpen(data.open);
            // Ensure button loses focus when menu closes
            if (!data.open && event.target instanceof HTMLElement) {
                event.target.blur();
            }
        },
        "aria-label": Strings.LeftNav_CreateAPlanOptionsMenuAriaLabel
    }, /*#__PURE__*/ React.createElement(MenuTrigger, {
        disableButtonEnhancement: true
    }, /*#__PURE__*/ React.createElement(Tooltip, {
        content: Strings.LeftNav_CreateOtherItemsTooltip,
        relationship: "label"
    }, /*#__PURE__*/ React.createElement(Button, {
        appearance: "primary",
        className: mergeClasses(styles.newPlanButtonBase, styles.splitButtonDropdown),
        "aria-haspopup": "menu",
        icon: /*#__PURE__*/ React.createElement(ChevronDown, {
            className: styles.dropdownChevronIcon
        })
    }))), /*#__PURE__*/ React.createElement(MenuPopover, {
        className: styles.menuPopover
    }, /*#__PURE__*/ React.createElement(MenuList, null, /*#__PURE__*/ React.createElement(Tooltip, {
        content: Strings.LeftNav_CreatePortfolioTooltip,
        relationship: "description"
    }, /*#__PURE__*/ React.createElement("div", {
        ref: portfolioMenuItemRef
    }, /*#__PURE__*/ React.createElement(MenuItem, {
        className: styles.menuItem,
        icon: /*#__PURE__*/ React.createElement(PortfolioFolderBundled, null),
        onClick: (event)=>{
            event.preventDefault();
            event.stopPropagation();
            newPlanInfo.onSelect("Portfolio");
        },
        role: "menuitem",
        "aria-label": Strings.LeftNav_CreateAPortfolioButtonText
    }, Strings.LeftNav_CreateAPortfolioButtonText))))))), newPlanInfo.isNewPortfolioCalloutVisible && /*#__PURE__*/ React.createElement(Popover, {
        positioning: {
            target: portfolioMenuItemRef.current,
            position: "after",
            align: "center"
        },
        open: newPlanInfo.isNewPortfolioCalloutVisible,
        onOpenChange: (event, data)=>{
            if (!data.open) {
                newPlanInfo.onDismissCallout();
            }
        },
        trapFocus: true,
        withArrow: true,
        trigger: /*#__PURE__*/ React.createElement("span", null),
        className: styles.calloutContent
    }, /*#__PURE__*/ React.createElement("div", {
        className: styles.calloutHeader
    }, /*#__PURE__*/ React.createElement("div", {
        className: styles.calloutTitle
    }, calloutDetails.calloutTitle), /*#__PURE__*/ React.createElement(Button, {
        appearance: "transparent",
        icon: /*#__PURE__*/ React.createElement(Dismiss, null),
        size: "small",
        onClick: (event)=>{
            event.stopPropagation();
            newPlanInfo.onDismissCallout();
        },
        className: styles.calloutDismissButton,
        "aria-label": Strings.LeftNav_CloseButtonAriaLabel
    })), /*#__PURE__*/ React.createElement("div", {
        className: styles.calloutDescription
    }, calloutDetails.calloutDescription), /*#__PURE__*/ React.createElement(Link, {
        href: LearnMoreLinks.linkToLearnMoreAboutPortfolioSubscription,
        target: "_blank",
        rel: "noopener noreferrer",
        className: styles.calloutLink
    }, calloutDetails.calloutLinkText)));
}
